import { useMemo } from 'react'
import classNames from 'classnames'
import IconBadge from 'components/icon-badge/IconBadge'
import { useTranslation } from 'next-i18next/pages'
import styles from '../RegistrationSideCard.module.scss'
import { checkHasInjectionUsageFields, checkHasSolarKva } from 'features/simulation/utils'
import { UsageSectionProps } from 'features/registration/components/registration-side-card/usage-section/types'
import { useStoreSelector } from 'hooks/store'
import SimulationRegistrationSideCardLayout from 'components/simulation-registration-side-card-layout/SimulationRegistrationSideCardLayout'
import { getTotalElectricityVolumeForMeterType, getTotalInjectionVolumeForMeterType } from 'utils/usage'

const UsageSection = ({ setShowEditUsageDrawer }: UsageSectionProps) => {
  // Redux store
  const { inputs } = useStoreSelector((store) => store.customer)
  const { simulation, meterDetails } = inputs

  // I18n
  const { t } = useTranslation(['simulationCard', 'common', 'simulation'])

  // Constants
  const totalElVolume = useMemo<number>(
    () => getTotalElectricityVolumeForMeterType(simulation.meterType, simulation.usage),
    [simulation.meterType, simulation.usage]
  )
  const hasKva = useMemo<boolean>(
    () =>
      checkHasSolarKva({
        counterType: meterDetails.counterType,
        hasSolarPanels: meterDetails.hasSolarPanels,
        walloniaSolarPanelsSince2024: meterDetails.walloniaSolarPanelsSince2024
      }),
    [meterDetails.counterType, meterDetails.hasSolarPanels, meterDetails.walloniaSolarPanelsSince2024]
  )
  const hasInjection = useMemo<boolean>(
    () => checkHasInjectionUsageFields({ counterType: meterDetails.counterType, hasSolarPanels: meterDetails.hasSolarPanels }),
    [meterDetails.counterType, meterDetails.hasSolarPanels]
  )
  const totalInjection = useMemo<number | null>(
    () =>
      hasInjection && simulation?.usage?.electricity?.injection
        ? getTotalInjectionVolumeForMeterType(simulation.meterType, simulation.usage.electricity.injection)
        : null,
    [hasInjection, simulation.meterType, simulation.usage]
  )

  return (
    <section className={classNames(styles.section, styles.usage)}>
      <div className={styles['section-header']}>
        <span className={styles['section-title']}>{t('simulationCard:estimatedUsage', 'Geschat verbruik')}</span>

        <SimulationRegistrationSideCardLayout.Action onClick={() => setShowEditUsageDrawer(true)}>
          {t('common:change', 'Wijzig')}
        </SimulationRegistrationSideCardLayout.Action>
      </div>
      <div className={styles['usage-badges']}>
        <IconBadge tooltipContent={t('simulation:usage.form.electricity')} label={`${totalElVolume} kWh`} icon="electricity" />

        {inputs.deliveryAddress.needsGas && (
          <IconBadge tooltipContent={t('simulation:usage.form.gas')} label={`${simulation.usage.gas} kWh`} icon="gas" />
        )}

        {typeof totalInjection === 'number' && (
          <IconBadge tooltipContent={t('simulation:usage.form.injection')} label={`${totalInjection} kWh`} icon="solarPanel" />
        )}

        {hasKva && (
          <IconBadge tooltipContent={t('simulation:usage.form.solarKva')} label={`${simulation.usage.solarKva} kVA`} icon="solarPanel" />
        )}
      </div>
    </section>
  )
}

export default UsageSection
